// src/shared/stickyList.js — фиксираните филтри и заглавният ред на списъците
// (ПРОМТ-37, Макс 29.10.2026: „лентата с филтрите остава, движат се само
// редовете").
//
// Защо тук, а не във всеки модул: списъците са строени по три начина —
// .filter-bar (фактури, проформи, одит…), търсачка в заглавието на панела
// (контакти, артикули) и само заглавие с „+ Нов…" (задачи, протоколи). Някои
// таблици са направо в панела, други в своя обвивка с плъзгане. Вместо 20
// поправки по модули рамката (shell.js) пуска ЕДИН наблюдател върху #page и
// той маркира всеки панел с таблица:
//   • лентата на панела получава класа .list-bar (position: sticky под горната
//     лента — височината ѝ е в --topbar-h);
//   • таблицата влиза в кутия .list-scroll с височина до долния ръб на екрана —
//     в нея се плъзгат САМО редовете, а заглавният ред (.sticky-head) стои
//     горе. Кутията плъзга и настрани: широките таблици не губят колони.
// Таблица, която вече е в своя обвивка с плъзгане (Такси, Одит, POS), не се
// мести — там заглавието ѝ залепва горе в собствената обвивка.
//
// Защо кутия, а не залепване към прозореца: panel е overflow:hidden (ъглите),
// а под 860 px — overflow-x:auto заради широките таблици. Всяко от двете
// прави панела отправна точка за sticky и заглавието спираше да се движи с
// прозореца. Кутията дава и двете — залепване и плъзгане настрани.
//
// На телефон (под 768 px) лентата показва само търсачката и бутон „Филтри ▾" —
// останалите филтри се свиват под него, за да не изядат екрана.

import { el } from './dom.js';

// Панел със списък: има таблица (не вложена в друга таблица) и лента отгоре.
function ownTables(panel) {
  return [...panel.querySelectorAll('table')].filter((t) =>
    t.closest('.panel') === panel && !(t.parentElement && t.parentElement.closest('table')));
}

// Коя е лентата: лентата с филтрите; иначе редът с търсачката; иначе
// заглавието на панела (там е „+ Нов…").
function pickBar(panel) {
  const kids = [...panel.children];
  return kids.find((k) => k.classList.contains('filter-bar'))
    || kids.find((k) => !k.classList.contains('list-scroll') && k.querySelector && k.querySelector('.searchbox') && !k.querySelector('table'))
    || kids.find((k) => k.classList.contains('panel-head'))
    || null;
}

function scrolls(node) {
  let cs;
  try { cs = getComputedStyle(node); } catch (e) { return false; }
  return /(auto|scroll)/.test((cs.overflowY || '') + ' ' + (cs.overflowX || '') + ' ' + (cs.overflow || ''));
}

function wrapTable(table, panel) {
  if (table.classList.contains('sticky-head') && (table.parentElement.classList.contains('list-scroll') || table.dataset.stickyLocal)) return;
  for (let n = table.parentElement; n && n !== panel; n = n.parentElement) {
    if (n.classList.contains('list-scroll')) break;
    if (scrolls(n)) { table.classList.add('sticky-head'); table.dataset.stickyLocal = '1'; return; }
  }
  if (!table.parentElement.classList.contains('list-scroll')) {
    const box = el('div', { class: 'list-scroll' });
    table.parentNode.insertBefore(box, table);
    box.appendChild(table);
  }
  table.classList.add('sticky-head');
}

// Височината на лентата → --list-bar-h на панела: кутията с редовете е
// висока до ръба на екрана МИНУС горната лента и тази.
function watchHeight(bar, panel) {
  const set = () => { const h = bar.offsetHeight; if (h) panel.style.setProperty('--list-bar-h', h + 'px'); };
  set();
  if (typeof ResizeObserver === 'function') new ResizeObserver(set).observe(bar);
}

// Колко филтъра (без търсачката) са включени — за надписа „Филтри (2) ▾".
function activeFilters(bar) {
  return [...bar.querySelectorAll('select, input')].filter((n) =>
    !n.closest('.searchbox') && n.type !== 'checkbox' && n.type !== 'radio' && String(n.value || '').trim() !== '').length;
}

// Под 768 px: търсачката остава, останалото е под „Филтри ▾". Само при
// лента с търсачка И поне още едно поле — иначе няма какво да се свива.
function collapsible(bar) {
  if (!bar.classList.contains('filter-bar') || bar.querySelector(':scope > .list-filters-toggle')) return;
  // Търсачката е пряко в лентата или в поле с надпис (регистрите — register.js).
  const search = [...bar.children].find((k) => k.classList.contains('searchbox') || (k.classList.contains('field') && k.querySelector(':scope > .searchbox')));
  if (search && search.classList.contains('field')) search.classList.add('list-bar-search');
  if (!search || !bar.querySelector('select, input:not(.searchbox-input)')) return;
  const btn = el('button', { type: 'button', class: 'btn sm ghost list-filters-toggle', 'aria-expanded': 'false' });
  const label = () => {
    const n = activeFilters(bar);
    const open = bar.classList.contains('filters-open');
    btn.textContent = 'Филтри' + (n ? ' (' + n + ')' : '') + (open ? ' ▴' : ' ▾');
  };
  btn.addEventListener('click', () => {
    const open = !bar.classList.contains('filters-open');
    bar.classList.toggle('filters-open', open);
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    label();
  });
  bar.addEventListener('change', label);
  bar.addEventListener('input', label);
  search.after(btn);
  bar.classList.add('list-bar-collapsible');
  label();
}

function enhancePanel(panel) {
  if (panel.closest('.modal')) return;
  const tables = ownTables(panel);
  if (!tables.length) return;
  const bar = pickBar(panel);
  if (!bar) return;
  panel.classList.add('list-panel');
  if (!bar.classList.contains('list-bar')) {
    bar.classList.add('list-bar');
    collapsible(bar);
    watchHeight(bar, panel);
  }
  tables.forEach((t) => wrapTable(t, panel));
}

// Еднократно обхождане (и за тестовете).
export function enhanceLists(root) {
  if (!root || !root.querySelectorAll) return;
  root.querySelectorAll('.panel').forEach(enhancePanel);
}

// Нужна ли е нова обиколка: само когато се е появила таблица, панел или
// лента. Пречертаните редове (tbody) не я пускат.
function relevant(records) {
  return records.some((r) => [...r.addedNodes].some((n) => n.nodeType === 1
    && (n.matches('table, .panel, .filter-bar, .panel-head, .searchbox') || (n.tagName !== 'TR' && n.querySelector && n.querySelector('table, .panel')))));
}

let _pageObserver = null;
// Вика се от рамката за всяка нова страница (#page се пресъздава при всяко
// пречертаване). Екраните зареждат данните си асинхронно — затова наблюдател,
// а не еднократно обхождане след view().
export function watchLists(page) {
  if (_pageObserver) _pageObserver.disconnect();
  _pageObserver = null;
  if (!page || typeof MutationObserver !== 'function') return;
  _pageObserver = new MutationObserver((records) => { if (relevant(records)) enhanceLists(page); });
  _pageObserver.observe(page, { childList: true, subtree: true });
  enhanceLists(page);
}

let _topObserver = null;
// Височината на горната лента → --topbar-h (на телефон тя се пренася на два
// реда; фиксираната лента на списъка застава точно под нея).
export function watchTopbar(topbar) {
  if (_topObserver) _topObserver.disconnect();
  _topObserver = null;
  if (!topbar) return;
  const set = () => { const h = topbar.offsetHeight; if (h) document.documentElement.style.setProperty('--topbar-h', h + 'px'); };
  if (typeof ResizeObserver === 'function') { _topObserver = new ResizeObserver(set); _topObserver.observe(topbar); }
  else set();
}
